import jwt_decode from 'jwt-decode';
import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, ReplaySubject } from 'rxjs';
import { AccessPermissions } from 'src/app/models/api/accountManagement.model';
import { DetailedAccessToken } from 'src/app/models/auth0/detailed-access-token';
import { v4 as uuidv4 } from 'uuid';
import { AccountManagementHttpService } from '../../httpServices/accountManagementHttpService/account-management-http-service.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FilterStorage } from '../../filters/filter-storage';
import { clearReturnUrl } from 'src/app/common/returnUrlUtils';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly _isLoggedIn$ = new ReplaySubject<boolean>(1);
  private _zenityAccountId: string;
  private _loggedInUser: import('@auth0/auth0-spa-js').User;
  private _userPermissions: string[] = [];
  private readonly _sessionId: string = uuidv4();
  private _userPermission: string;
  private _userRoles: string[] = [];
  private _accountName: string;
  private _apiUserRoles: string[] = [];
  private _accessPermissions: AccessPermissions = {};

  constructor(
    public auth: AuthService,
    private readonly _accountManagementHttpService: AccountManagementHttpService
  ) {
    this.auth.user$.subscribe(
      (user) => {
        if (user && !this.loggedInUser) {
          this._loggedInUser = user as import('@auth0/auth0-spa-js').User;
          this._zenityAccountId = user['org_id'] as string;
          this.auth.getAccessTokenSilently().subscribe(
            (token) => {
              const decoded_jwt = jwt_decode<DetailedAccessToken>(token);
              const scope = decoded_jwt.scope;
              this._accountName = decoded_jwt.accountName ?? 'Unknown';
              this._userPermissions = scope.split(' ');
              this._getUserPermission(this._userPermissions);
              this._extractUserRoles(this._userPermissions);
              this._fetchUserDetailsRoles();
            },
            () => this._isLoggedIn$.next(false)
          );
        }
      },
      () => this._isLoggedIn$.next(false)
    );
  }

  public get sessionId(): string {
    return this._sessionId;
  }

  public get accountName(): string {
    return this._accountName;
  }

  public get userPermissions(): string[] {
    return this._userPermissions;
  }

  public get userPermission(): string {
    return this._userPermission;
  }

  public get userRoles(): string[] {
    return this._userRoles;
  }

  public get apiUserRoles(): string[] {
    return this._apiUserRoles;
  }

  public get accessPermissions(): AccessPermissions {
    return this._accessPermissions;
  }

  public get loggedInUser(): import('@auth0/auth0-spa-js').User {
    return this._loggedInUser;
  }

  public get zenityAccountId(): string {
    return this._zenityAccountId;
  }

  public canLoadApp(): Observable<boolean> {
    return this._isLoggedIn$;
  }

  public logout(): void {
    clearReturnUrl();
    FilterStorage.clearAll();
    this.auth.logout();
  }

  private _logoutToNoAccess(): void {
    clearReturnUrl();
    this.auth.logout({ openUrl: false }).subscribe(() => {
      window.location.href = '/no-access';
    });
  }

  private _isAdminFromToken(): boolean {
    return this._userRoles.some((role) => role.toLowerCase() === 'admin');
  }

  private _getUserPermission(permissions: string[]): void {
    for (const permissionIndex in permissions) {
      if (permissions[permissionIndex].startsWith('user')) {
        const splittedPermission = permissions[permissionIndex].split(':');
        this._userPermission = splittedPermission[1];
        return;
      }
    }
  }

  private _extractUserRoles(permissions: string[]): void {
    const roles = permissions
      .filter((permission) => permission.startsWith('user:'))
      .map((permission) => permission.split(':')[1]);

    this._userRoles = [...new Set(roles)];
  }

  private _fetchUserDetailsRoles(): void {
    this._accountManagementHttpService.getUserDetails().subscribe({
      next: (response) => {
        this._accessPermissions = response.accessPermissions ?? {};
        const isAccessGroupsEnabled = response.featureFlags?.some(
          (ff) => ff.key === 'core_rbac_access_management' && ff.value
        );
        const isAdminFromRoles = response.roles.some((role) => role.toLowerCase() === 'admin');

        if (this._isAdminFromToken() || isAdminFromRoles) {
          this._apiUserRoles = ['Admin'];
          this._isLoggedIn$.next(true);
        } else if (response.roles.length === 0) {
          this._apiUserRoles = [];
          const hasNoAccessPermissions = Object.keys(this._accessPermissions).length === 0;
          if (isAccessGroupsEnabled && hasNoAccessPermissions) {
            this._logoutToNoAccess();
            return;
          }
          this._isLoggedIn$.next(true);
        } else {
          const uniqueRoles = [...new Set(response.roles)];
          this._apiUserRoles = uniqueRoles.sort((a, b) => a.localeCompare(b));
          this._isLoggedIn$.next(true);
        }
      },
      error: (error: HttpErrorResponse) => {
        this._apiUserRoles = [];
        this._accessPermissions = {};

        if (error.status === 403 && !this._isAdminFromToken()) {
          this._logoutToNoAccess();
        } else {
          this._isLoggedIn$.next(false);
        }
      },
    });
  }
}
